/**
 * BT-2092 (M4S-5696): the order a custom variable's banner columns are shown in, and moving one.
 *
 * A variable's banner columns are its distinct bucket labels (several buckets with one label are one
 * column: a nest and the iOOH base both make them), or its range labels, first seen first. Moving a
 * column never reorders the buckets: in a Logic variable consecutive buckets joined by AND count
 * their intersection, so the bucket order is part of what each column counts, and a nest's recipe
 * matches its buckets only in the order it generated them. The order is kept on the variable instead
 * (`CustomVariable.columnOrder`, by label) and applied where the columns are made
 * (`variableColumns`, custom-var-answers.ts) and where the rail lists them, after everyone is
 * counted. It changes letters, never a number.
 *
 * - A label the order does not name (a column added since) follows the named ones, in its own order.
 * - A named label the variable no longer has is ignored.
 * - The order is stored only when it differs from the variable's own, so moving a column back
 *   leaves the variable as it was (the Run check compares the variables as JSON).
 * - The Other column is not one of these: it always ends the variable's group.
 *
 * Pure: no React, no requests.
 */
import type { CustomVariable } from '@/app/(private)/ai/components/Crosstabs/crosstab-types';

/** Distinct values, first seen first. */
function distinct(labels: readonly string[]): string[] {
    return [...new Set(labels)];
}

/** The variable's column labels in its own order (bucket or range order), before any move. */
export function naturalColumnLabels(cv: CustomVariable): string[] {
    if (cv.kind === 'logic' || cv.kind === 'categorical-bucketing') {
        return distinct(cv.buckets.map((b) => b.label));
    }
    if (cv.kind === 'bucketing') return distinct(cv.ranges.map((r) => r.label));
    return [];
}

/** `natural` in `order`: the named labels it has, in that order, then the rest in their own. */
export function orderColumnLabels(
    natural: readonly string[],
    order: readonly string[] | undefined,
): string[] {
    if (!order?.length) return [...natural];
    const has = new Set(natural);
    const named = distinct(order).filter((label) => has.has(label));
    const placed = new Set(named);
    return [...named, ...natural.filter((label) => !placed.has(label))];
}

/** The variable's banner column labels, in the order they are shown. */
export function variableColumnLabels(cv: CustomVariable): string[] {
    return orderColumnLabels(naturalColumnLabels(cv), cv.columnOrder);
}

/** `columns` (one per label) in the variable's shown order. */
export function inColumnOrder<T extends { label: string }>(cv: CustomVariable, columns: T[]): T[] {
    if (!cv.columnOrder?.length) return columns;
    const rank = new Map(
        orderColumnLabels(
            columns.map((c) => c.label),
            cv.columnOrder,
        ).map((label, i) => [label, i]),
    );
    return [...columns].sort((a, b) => (rank.get(a.label) ?? 0) - (rank.get(b.label) ?? 0));
}

/** Whether the variable's banner columns can be moved: a kind that has them, two or more of them. */
export function canOrderColumns(cv: CustomVariable): boolean {
    return naturalColumnLabels(cv).length > 1;
}

/** `cv` keeping `order` only when it differs from its own order. */
function withOrder(cv: CustomVariable, order: string[]): CustomVariable {
    const natural = naturalColumnLabels(cv);
    const same = order.length === natural.length && order.every((label, i) => label === natural[i]);
    const next: CustomVariable = { ...cv };
    if (same) delete next.columnOrder;
    else next.columnOrder = order;
    return next;
}

/**
 * `cv` with the column `label` moved to position `to` (0 is first) among its columns. Unchanged
 * when it has no such column or `to` is out of range.
 */
export function withColumnMoved(cv: CustomVariable, label: string, to: number): CustomVariable {
    const shown = variableColumnLabels(cv);
    const from = shown.indexOf(label);
    if (from < 0 || to < 0 || to >= shown.length || from === to) return cv;
    const order = [...shown];
    order.splice(from, 1);
    order.splice(to, 0, label);
    return withOrder(cv, order);
}

/**
 * `next` (the variable as an edit saved it) keeping the column order `prev` had. The dialogs build
 * a variable without it. A column renamed in place, the same buckets in the same places with one
 * label changed throughout, keeps its position under its new label. An edit that put the columns
 * in another order itself (the Nest tab moves a layer's members) is the newer word: its order wins
 * and the moved order is dropped.
 */
export function keepColumnOrder(prev: CustomVariable, next: CustomVariable): CustomVariable {
    if (!prev.columnOrder?.length || next.columnOrder?.length) return next;
    const before =
        prev.kind === 'bucketing'
            ? prev.ranges.map((r) => r.label)
            : prev.buckets.map((b) => b.label);
    const after =
        next.kind === 'bucketing'
            ? next.ranges.map((r) => r.label)
            : next.buckets.map((b) => b.label);
    const renamed = new Map<string, string>();
    if (prev.kind === next.kind && before.length === after.length) {
        const pairs = new Map<string, Set<string>>();
        before.forEach((label, i) => {
            const set = pairs.get(label) ?? new Set<string>();
            set.add(after[i]);
            pairs.set(label, set);
        });
        for (const [label, to] of pairs) {
            const [only] = [...to];
            // One new label for every bucket that had this one, and not a label it already had.
            if (to.size === 1 && only !== label && !before.includes(only)) renamed.set(label, only);
        }
    }
    // The columns both versions have, each in its own version's order: when those differ, the edit
    // reordered the columns itself.
    const was = naturalColumnLabels(prev).map((label) => renamed.get(label) ?? label);
    const now = naturalColumnLabels(next);
    const kept = was.filter((label) => now.includes(label));
    const keptNow = now.filter((label) => was.includes(label));
    if (kept.some((label, i) => label !== keptNow[i])) return next;
    const order = prev.columnOrder.map((label) => renamed.get(label) ?? label);
    return withOrder(next, orderColumnLabels(now, order));
}
